import React from 'react';
import { STATUS, INK, FONT_MONO } from 'Utils/styleTokens';
import { getDisplayDate } from 'Utils';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

/**
 * Sub-line under a sensitive count: the total it came out of. A total with no sensitive
 * count means the source was profiled but never discovered — say so rather than letting a
 * bare "—" over a denominator read as "zero sensitive".
 *
 * `processed` marks a denominator that only covers what the run actually sampled — the worker
 * skips zero-row tables pre-flight, so the total is not the whole schema and saying "of 0 tables"
 * flat would be wrong. The skipped count is named on its own line (see `foot` below).
 */
const outOf = (sensitive, total, noun, { processed = false } = {}) => {
  if (total == null) return 'not discovered yet';
  return `of ${fmt(total)} ${noun}${processed ? ' processed' : ''}${sensitive == null ? ' · not discovered yet' : ''}`;
};

const DEFAULT_NOMENCLATURE = { item: 'tables', itemSingular: 'table' };

const RISK_COLORS = {
  CRITICAL: STATUS.danger.text,
  HIGH: STATUS.orange.text,
  MEDIUM_HIGH: STATUS.orange.text,
  MEDIUM: STATUS.info.text,
  MED_LOW: STATUS.info.text,
  LOW: STATUS.success.text,
  UNKNOWN: STATUS.neutral.text,
};

function Kpi({ label, value, sub, foot, valueColor, testId }) {
  return (
    <div className='sg-stat-tile' style={{ padding: '14px 16px' }} data-testid={testId}>
      <div className='sg-stat-tile__label'>{label}</div>
      <div className='sg-stat-tile__value' style={{ fontSize: 22, ...(valueColor ? { color: valueColor } : {}) }}>
        {value}
      </div>
      <div className='sg-stat-tile__sub'>{sub}</div>
      {foot && <div className='sg-stat-tile__sub' style={{ color: INK[400] }}>{foot}</div>}
    </div>
  );
}

/**
 * Data Source Hub KPI strip — mockup data-source-hub. Five top-line facts from
 * metadata/summary + the identity cluster + the latest discovery risk.
 * Renders nothing until at least one feed resolves.
 *
 * The Risk level tile carries what the retired "Current risk" thermometer panel used
 * to say (element-type count + the risk level's categories), so that copy has one home.
 * Item/field counts prefer the discovery run's totals and fall back to the meta profile.
 */
function HubKpiStrip({
  riskLabel,
  cluster,
  currentMember,
  summary,
  findingTypeCount,
  riskCategories,
  tableCount,
  emptyTableCount,
  fieldCount,
  sensitiveTableCount,
  sensitiveFieldCount,
  nomenclature,
}) {
  if (!summary && !cluster && !riskLabel) return null;

  const nouns = nomenclature || DEFAULT_NOMENCLATURE;

  const tables = Array.isArray(summary?.tables) ? summary.tables : [];
  const profiledFieldCount = tables.reduce((acc, t) => acc + (Number(t.columnCount) || 0), 0);
  const items = tableCount != null ? tableCount : summary ? summary.tableCount : null;
  const fields = fieldCount != null ? fieldCount : summary ? profiledFieldCount : null;
  const categories = Array.isArray(riskCategories) ? riskCategories : [];
  const emptyTables = Number(emptyTableCount) || 0;
  const matchPct =
    currentMember?.similarityToRepresentative != null
      ? `${Math.round(currentMember.similarityToRepresentative * 100)}%`
      : '—';

  return (
    <div className='mb-3' style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
      <Kpi
        label='Risk level'
        value={(riskLabel || 'UNKNOWN').replace(/_/g, ' ')}
        valueColor={RISK_COLORS[riskLabel] || RISK_COLORS.UNKNOWN}
        sub={
          findingTypeCount != null ? (
            <span data-testid='hub-kpi-risk-sub'>
              <span className='accent'>{fmt(findingTypeCount)}</span> sensitive element types found
              {categories.length ? `, including ${categories.join(', ')}` : ''}
            </span>
          ) : (
            'not discovered yet'
          )
        }
      />
      <Kpi
        label='Schema match'
        value={matchPct}
        sub={cluster?.displayName ? `to ${cluster.displayName}` : 'not clustered yet'}
      />
      {/* The headline number is the sensitive count — that is the fact this page exists to
          report. The total is the denominator underneath it. */}
      <Kpi
        label={`Sensitive ${nouns.item}`}
        value={sensitiveTableCount != null ? fmt(sensitiveTableCount) : '—'}
        sub={outOf(sensitiveTableCount, items, nouns.item, { processed: items != null })}
        foot={emptyTables > 0
          ? `${fmt(emptyTables)} empty ${emptyTables === 1 ? nouns.itemSingular : nouns.item} skipped`
          : null}
        testId='hub-kpi-sensitive-items'
      />
      <Kpi
        label='Sensitive fields'
        value={sensitiveFieldCount != null ? fmt(sensitiveFieldCount) : '—'}
        sub={outOf(sensitiveFieldCount, fields, 'fields')}
      />
      <Kpi
        label='Last profiled'
        value={
          summary?.snapshotTakenAt ? (
            <span style={{ fontFamily: FONT_MONO, fontSize: 18 }}>{getDisplayDate(summary.snapshotTakenAt)}</span>
          ) : (
            'never'
          )
        }
        sub={
          cluster?.dataSources?.length
            ? `${cluster.dataSources.length} cluster member${cluster.dataSources.length === 1 ? '' : 's'}`
            : 'run a profile to cluster'
        }
      />
    </div>
  );
}

export default HubKpiStrip;
